interface Props {
  values: number[];
  width?: number;
  height?: number;
  color?: string;
  fill?: string;
  strokeWidth?: number;
}

/**
 * Minimal SVG sparkline. No external chart library. Renders a polyline + fill area.
 * If values is all-zero, draws a flat baseline so the card doesn't look broken.
 * Es decorativa (la cifra va escrita al lado): se oculta a los lectores de pantalla.
 * Con `preserveAspectRatio="none"` se estira al ancho que le de su contenedor.
 */
export function Sparkline({ values, width = 200, height = 48, color = '#1f2937', fill = 'rgba(31, 41, 55, 0.08)', strokeWidth = 1.5 }: Props) {
  if (values.length === 0) {
    return <svg width={width} height={height} aria-hidden="true" />;
  }
  const max = Math.max(1, ...values);
  const step = values.length > 1 ? width / (values.length - 1) : 0;
  const points = values.map((v, i) => {
    const x = i * step;
    const y = height - (v / max) * (height - 4) - 2;
    return [x, y] as [number, number];
  });
  const linePath = points.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const areaPath = `${linePath} L${(points[points.length - 1][0]).toFixed(1)},${height} L0,${height} Z`;
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" style={{ display: 'block', overflow: 'visible' }} aria-hidden="true">
      <path d={areaPath} fill={fill} />
      <path d={linePath} fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

/**
 * Two-series version: primary line on top, secondary muted line below.
 * Used e.g. for calls (total vs connected) or emails (sent vs replied).
 */
export function SparklineDual({
  total, secondary, width = 200, height = 48,
  primaryColor = '#1f2937', secondaryColor = '#10b981', fill = 'rgba(31,41,55,0.06)',
}: { total: number[]; secondary: number[]; width?: number; height?: number; primaryColor?: string; secondaryColor?: string; fill?: string }) {
  if (total.length === 0) return <svg width={width} height={height} />;
  const max = Math.max(1, ...total, ...secondary);
  const step = total.length > 1 ? width / (total.length - 1) : 0;

  const toPath = (vals: number[]): string =>
    vals.map((v, i) => {
      const x = i * step;
      const y = height - (v / max) * (height - 2) - 1;
      return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
    }).join(' ');

  const totalPath = toPath(total);
  const secondaryPath = toPath(secondary);
  const lastX = (total.length - 1) * step;
  const areaPath = `${totalPath} L${lastX.toFixed(1)},${height} L0,${height} Z`;

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" style={{ display: 'block' }}>
      <path d={areaPath} fill={fill} />
      <path d={totalPath} fill="none" stroke={primaryColor} strokeWidth={1.5} strokeLinejoin="round" strokeLinecap="round" opacity={0.5} />
      <path d={secondaryPath} fill="none" stroke={secondaryColor} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}
